import { STAFF_ROLE_LABELS_PL } from '@cfcg/shared';
import { useState } from 'react';
import { Alert, Button } from './components/ui.js';
import { messageOf, useCurrentUser, useLogout } from './lib/auth.js';
import { AccountPage } from './pages/AccountPage.js';
import { CatalogPage } from './pages/CatalogPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { StaffPage } from './pages/StaffPage.js';

// Przełączanie widoków stanem, bez routera. Panel ma na razie trzy ekrany,
// a dokładanie react-routera poza uzgodnionym stackiem nie ma uzasadnienia.
// Prawdziwa nawigacja z adresami URL dojdzie w Etapie 6 razem z kreatorem presetu.
type View = 'catalog' | 'staff' | 'account';

export function App() {
  const { data: current, isPending, error } = useCurrentUser();
  const logout = useLogout();
  const [view, setView] = useState<View>('catalog');

  if (isPending) {
    return <p className="p-8 text-sm text-neutral-500">Wczytywanie…</p>;
  }

  if (error) {
    return (
      <main className="mx-auto max-w-md p-8">
        <Alert tone="error">{messageOf(error)}</Alert>
      </main>
    );
  }

  if (!current) return <LoginPage />;

  const isAdmin = current.user.role === 'admin';
  const tabs: { id: View; label: string }[] = [
    { id: 'catalog', label: 'Katalog ustawień' },
    ...(isAdmin ? [{ id: 'staff' as const, label: 'Pracownicy' }] : []),
    { id: 'account', label: 'Moje konto' },
  ];

  return (
    <div className="min-h-dvh bg-neutral-50">
      <header className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 p-4">
          <div>
            <h1 className="font-semibold text-neutral-900">Konfigurator Cloudflare</h1>
            <p className="text-xs text-neutral-500">
              {current.user.displayName} · {STAFF_ROLE_LABELS_PL[current.user.role]}
            </p>
          </div>
          <Button variant="secondary" onClick={() => logout.mutate()} disabled={logout.isPending}>
            Wyloguj
          </Button>
        </div>

        <nav className="mx-auto flex max-w-5xl gap-1 px-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setView(tab.id)}
              aria-current={view === tab.id ? 'page' : undefined}
              className={`-mb-px border-b-2 px-3 py-2 text-sm transition ${
                view === tab.id
                  ? 'border-neutral-900 font-medium text-neutral-900'
                  : 'border-transparent text-neutral-500 hover:text-neutral-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-5xl p-4 py-6">
        {view === 'catalog' && <CatalogPage />}
        {view === 'staff' && isAdmin && <StaffPage currentUserId={current.user.id} />}
        {view === 'account' && <AccountPage current={current} />}
      </main>
    </div>
  );
}
